import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, type Signal } from '@angular/core';
import {
  GenericLinkDirective,
  GenericRichTextDirective,
  TextInterpolationPipe,
} from '@core/jss-utils';
import type { AsyncState } from '@essent/common';
import {
  CrntButton,
  CrntCallout,
  CrntFormControl,
  CrntFormControlLabel,
  CrntFormControlValidationMessage,
  CrntIcon,
  CrntRadio,
  CrntRadioGroup,
  CrntTextInput,
} from '@essent/current-web-angular';
import {
  activateSmartChargingLeadStep,
  CHARGING_STATION_FORM_ID,
  getSalesFunnelVM,
  getSmartChargingLeadChargingStationFormState,
  getSmartChargingLeadLicensePlateCheckFormState,
  getSmartChargingLeadWidgetStep,
  LICENSE_PLATE_CHECK_FORM_ID,
  sendSmartChargingLead,
  SmartChargingLeadWidgetStep,
} from '@innogy/become-a-customer/state';
import { DefaultFormComponentModule } from '@innogy/core/modules/components';
import type { LicensePlateLookupResponse } from '@integration/api-client/smart-charging';
import { CardComponent } from '@legacy/common-ui-partials';
import { Store } from '@ngrx/store';
import { selectLicensePlateLookupState } from '@smart-charging/store';
import { MarkAsSubmittedAction } from 'ngrx-forms';

import {
  mapSmartChargingLeadWidgetContent,
  type SmartChargingLeadWidgetRendering,
} from './smart-charging-lead-widget.rendering';

@Component({
  selector: 'wl-smart-charging-lead-widget',
  templateUrl: './smart-charging-lead-widget.component.html',
  styleUrls: [
    './smart-charging-lead-widget.component.essent.scss',
    './smart-charging-lead-widget.component.ed.scss',
  ],
  standalone: true,
  imports: [
    CardComponent,
    CrntButton,
    CrntCallout,
    CrntRadioGroup,
    CrntRadio,
    CrntFormControlValidationMessage,
    CrntFormControlLabel,
    CrntIcon,
    CrntFormControl,
    CrntTextInput,
    DefaultFormComponentModule,
    GenericRichTextDirective,
    GenericLinkDirective,
    NgTemplateOutlet,
    TextInterpolationPipe,
  ],
})
export class SmartChargingLeadWidgetComponent {
  readonly store$ = inject(Store);

  readonly rendering = input.required<SmartChargingLeadWidgetRendering>();

  readonly salesFunnelVM = this.store$.selectSignal(getSalesFunnelVM);
  readonly chargingStationForm = this.store$.selectSignal(
    getSmartChargingLeadChargingStationFormState
  );
  readonly licensePlateCheckForm = this.store$.selectSignal(
    getSmartChargingLeadLicensePlateCheckFormState
  );
  readonly step = this.store$.selectSignal(getSmartChargingLeadWidgetStep);
  readonly licensePlateLookupState: Signal<
    AsyncState<LicensePlateLookupResponse>
  > = this.store$.selectSignal(selectLicensePlateLookupState);

  readonly content = computed(() =>
    mapSmartChargingLeadWidgetContent(this.rendering())
  );

  readonly stepIndicator = computed(() => ({
    step: [
      SmartChargingLeadWidgetStep.ChargingStationForm,
      SmartChargingLeadWidgetStep.LicensePlateRejected,
    ].includes(this.step())
      ? 1
      : 2,
    of: this.content().of,
    steps: 2,
  }));

  readonly licensePlateLookupResult = computed(
    () => this.licensePlateLookupState().data?.support_status
  );

  submitChargingStationForm(): void {
    this.store$.dispatch(new MarkAsSubmittedAction(CHARGING_STATION_FORM_ID));
  }

  submitLicensePlateCheckForm(): void {
    this.store$.dispatch(
      new MarkAsSubmittedAction(LICENSE_PLATE_CHECK_FORM_ID)
    );
  }

  submitLead(): void {
    this.store$.dispatch(sendSmartChargingLead());
  }

  stepBack(): void {
    if (
      [
        SmartChargingLeadWidgetStep.LicensePlateAccepted,
        SmartChargingLeadWidgetStep.LicensePlateRejected,
        SmartChargingLeadWidgetStep.LicensePlateUnknown,
      ].includes(this.step())
    ) {
      this.store$.dispatch(
        activateSmartChargingLeadStep({
          step: SmartChargingLeadWidgetStep.LicensePlateForm,
        })
      );
    } else {
      this.store$.dispatch(
        activateSmartChargingLeadStep({
          step: SmartChargingLeadWidgetStep.ChargingStationForm,
        })
      );
    }
  }

  readonly SmartChargingLeadWidgetStep = SmartChargingLeadWidgetStep;
}
